import { MuiBox, MuiTypography } from '@/features/shared/material-ui';
import { isEmpty } from 'lodash';
import { getOr } from 'lodash/fp';
import React, { memo, useMemo } from 'react';
import { useSDLContent } from '../hooks/useSDLContent';
import { sdlContentSpecParser } from '../utils/SDLContent.utils';
import { additionalDefaultInfoSpec, deviceTypeAdditionalInfoSpecMap } from './Additional.constants';
import { getConnectionType, getMotionDetection } from './Additional.mapper';

const AdditionalInformation = ({ content, labels, deviceType }: any) => {
  const { parseContentRawProp } = useSDLContent(content);

  const { additionalInformation, basicInformation } = useMemo(
    () => ({
      additionalInformation: parseContentRawProp('additional_information'),
      basicInformation: parseContentRawProp('basic_information'),
      firmwareInformation: parseContentRawProp('firmware_information'),
    }),
    [parseContentRawProp],
  );

  const connectionType = useMemo(
    () => getConnectionType({ data: additionalInformation }),
    [additionalInformation],
  );
  const motionDetection = useMemo(
    () => getMotionDetection({ data: additionalInformation }),
    [additionalInformation],
  );

  const isObj = (obj: any) => {
    return Object.prototype.toString.call(obj) === '[object Object]';
  };
  const isArr = (arr: any) => {
    return Array.isArray(arr);
  };

  const additionalInfo = useMemo(() => {
    const deviceTypeAdditionalInfoSpec = getOr(
      additionalDefaultInfoSpec,
      deviceType,
      deviceTypeAdditionalInfoSpecMap,
    ); // Get info spec by device

    return sdlContentSpecParser(
      {
        additionalInformation,
        basicInformation,
        connectionType,
        motionDetection,
      },
      deviceTypeAdditionalInfoSpec,
    );
  }, [additionalInformation, basicInformation, connectionType, deviceType, motionDetection]);

  // Note: The default values "N/A" now are applied in the infoSpec
  const renderItem = ({ key }: { key: string }) => {
    const fieldValue = (additionalInfo as any)[key];

    if (isObj(fieldValue)) {
      return Object.keys(fieldValue).map((key2: any) => (
        <div key={key + Math.random()}>
          {labels[key2]}: {fieldValue[key2] || 'N/A'}
        </div>
      ));
    }
    // Custom fields.
    if (isArr(fieldValue) && !isEmpty(fieldValue)) {
      return fieldValue.map((keyA: any) =>
        !isObj(keyA) ? (
          <div key={key + Math.random()}>{keyA}</div>
        ) : (
          Object.keys(keyA).map((key2: any) => (
            <div key={key + Math.random()}>
              <div>
                <strong>{labels[key2]}</strong>
              </div>
              {keyA[key2].map((value: any) => (
                <div key={key + Math.random() + 1}>{value}</div>
              ))}
            </div>
          ))
        ),
      );
    }

    if (!isObj(fieldValue) && !isArr(fieldValue)) {
      return fieldValue || 'N/A';
    }

    return 'N/A';
  };

  return (
    <MuiBox display="flex" flexDirection="column" flexWrap="wrap" className="basic-additional-data">
      {additionalInfo &&
        Object.keys(additionalInfo).map((key) => (
          <div key={key + Math.random()}>
            <MuiTypography
              variant="overline"
              component="h6"
              role="heading"
              data-testid="sdl-title"
              data-title-key={key}
            >
              {labels[key]}
            </MuiTypography>

            <ul>
              <li key={key + Math.random()}>{renderItem({ key })}</li>
            </ul>
          </div>
        ))}
    </MuiBox>
  );
};

export default memo(AdditionalInformation);
